/**
 * Sub-spec 11 Plan 5 — /account/privacy customer page.
 *
 * Four sections per spec §17 / brainstorm D7:
 *   1. Your consents — waiver acceptances + opt-in history.
 *   2. Communication preferences — link to /account/notifications.
 *   3. Data export — POST /api/customer/account/data-export.
 *   4. Delete account — multi-step erasure with 24h cancel window.
 */
import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandedShell } from '../components/BrandedShell';
import { CustomerApiError, customerApi } from '../customer_api';

interface ConsentsResponse {
  waiverAcceptances: Array<{
    id: string;
    waiverVersion: number;
    signatureText: string;
    acceptedAt: string;
  }>;
  optInRecords: Array<{
    id: string;
    category: string;
    channel: string;
    allowed: number;
    source: string;
    createdAt: string;
  }>;
  suppressions: Array<{
    id: string;
    channel: string;
    reason: string | null;
    createdAt: string;
  }>;
  notificationPreferences: Array<{
    category: string;
    channel: string;
    enabled: number;
  }>;
}

interface ExportResponse {
  fileId: string;
  archiveSize: number;
  signedUrl: string;
  expiresAt: string;
}

interface DeleteStatusResponse {
  status: 'none' | 'pending' | 'cancelled' | 'completed' | 'failed';
  scheduledFor?: string;
  requestedAt?: string;
  cancelledAt?: string | null;
  executedAt?: string | null;
}

const sectionCard: CSSProperties = {
  background: 'var(--surface, white)',
  border: '1px solid var(--border, oklch(0.92 0.005 80))',
  borderRadius: '12px',
  padding: '1.25rem 1.5rem',
  marginTop: '1.25rem',
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function Privacy() {
  const [consents, setConsents] = useState<ConsentsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportResult, setExportResult] = useState<ExportResponse | null>(null);
  const [deleteStatus, setDeleteStatus] = useState<DeleteStatusResponse | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0);

  const loadConsents = useCallback(() => {
    customerApi<ConsentsResponse>('/api/customer/privacy/consents')
      .then(setConsents)
      .catch((err) => {
        setLoadError(
          err instanceof CustomerApiError ? `Could not load consents (${err.status})` : String(err),
        );
      });
  }, []);

  const loadDeleteStatus = useCallback(() => {
    customerApi<DeleteStatusResponse>('/api/customer/account/delete/status')
      .then(setDeleteStatus)
      .catch(() => {
        /* status endpoint is optional surface — ignore */
      });
  }, []);

  useEffect(() => {
    loadConsents();
    loadDeleteStatus();
  }, [loadConsents, loadDeleteStatus]);

  async function handleRequestExport() {
    setExportBusy(true);
    setExportError(null);
    setExportResult(null);
    try {
      const res = await customerApi<ExportResponse>('/api/customer/account/data-export', {
        method: 'POST',
      });
      setExportResult(res);
    } catch (err) {
      if (err instanceof CustomerApiError && err.status === 429) {
        setExportError('You can request one data export per 24 hours. Please try again later.');
      } else if (err instanceof CustomerApiError && err.status === 503) {
        setExportError('Data exports are not available right now. Please contact support.');
      } else {
        setExportError(
          err instanceof CustomerApiError
            ? `Export failed (${err.status})`
            : 'Export failed. Please try again.',
        );
      }
    } finally {
      setExportBusy(false);
    }
  }

  async function handleDelete() {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await customerApi('/api/customer/account/delete', { method: 'POST' });
      setDeleteConfirmStep(0);
      loadDeleteStatus();
    } catch (err) {
      setDeleteError(
        err instanceof CustomerApiError
          ? `Delete request failed (${err.status})`
          : 'Delete request failed. Please try again.',
      );
    } finally {
      setDeleteBusy(false);
    }
  }

  async function handleCancelDelete() {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await customerApi('/api/customer/account/delete/cancel', { method: 'POST' });
      loadDeleteStatus();
    } catch (err) {
      setDeleteError(
        err instanceof CustomerApiError
          ? `Cancel failed (${err.status})`
          : 'Cancel failed. Please try again.',
      );
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <BrandedShell>
      <div
        style={{
          minHeight: '100dvh',
          background: 'var(--background, oklch(0.98 0.005 80))',
          color: 'var(--foreground, oklch(0.19 0.012 75))',
        }}
      >
        <div className="mx-auto max-w-2xl px-4 pt-6 pb-20 sm:px-6 sm:pt-10">
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.25rem' }}>
            Your privacy
          </h1>
          <p
            style={{
              color: 'var(--muted-foreground, oklch(0.45 0.005 80))',
              fontSize: '0.9rem',
            }}
          >
            Review your consents, download your data, or delete your account.
          </p>

          {loadError !== null && (
            <p
              role="alert"
              style={{ color: 'var(--destructive, oklch(0.55 0.18 30))', marginTop: '1rem' }}
            >
              {loadError}
            </p>
          )}

          {/* 1. Your consents */}
          <section style={sectionCard} aria-label="Your consents">
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              Your consents
            </h2>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem' }}>
              Waiver acceptances
            </h3>
            {consents === null ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>Loading…</p>
            ) : consents.waiverAcceptances.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>
                You have not accepted any waiver versions yet.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85rem' }}>
                {consents.waiverAcceptances.map((w) => (
                  <li key={w.id} style={{ padding: '0.25rem 0' }}>
                    Version {w.waiverVersion} — signed “{w.signatureText}” on{' '}
                    {fmtDate(w.acceptedAt)}
                  </li>
                ))}
              </ul>
            )}

            <h3
              style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                marginTop: '1rem',
                marginBottom: '0.4rem',
              }}
            >
              Marketing + reminder opt-ins
            </h3>
            {consents === null ? null : consents.optInRecords.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>
                No opt-in history recorded.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85rem' }}>
                {consents.optInRecords.map((r) => (
                  <li key={r.id} style={{ padding: '0.25rem 0' }}>
                    {r.category} × {r.channel}:{' '}
                    <strong>{r.allowed ? 'opted in' : 'opted out'}</strong> via {r.source} on{' '}
                    {fmtDate(r.createdAt)}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 2. Communication preferences */}
          <section style={sectionCard} aria-label="Communication preferences">
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Communication preferences
            </h2>
            <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              Change which reminders and marketing messages we send you.
            </p>
            <Link
              to="/account/notifications"
              style={{ color: 'var(--primary, oklch(0.4 0.15 254))', fontSize: '0.9rem' }}
            >
              Manage notification preferences →
            </Link>
          </section>

          {/* 3. Data export */}
          <section style={sectionCard} aria-label="Data export">
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Download a copy of your data
            </h2>
            <p style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              We will create a downloadable archive of everything we hold on you — bookings, pets,
              payments, consents, and message history. A link will arrive by email.
            </p>
            <button
              type="button"
              onClick={() => void handleRequestExport()}
              disabled={exportBusy}
              style={{
                padding: '0.6rem 1.2rem',
                background: 'var(--primary, oklch(0.4 0.15 254))',
                color: 'white',
                borderRadius: '999px',
                border: 'none',
                fontSize: '0.9rem',
                cursor: exportBusy ? 'not-allowed' : 'pointer',
                opacity: exportBusy ? 0.6 : 1,
              }}
            >
              {exportBusy ? 'Preparing…' : 'Request a copy of my data'}
            </button>
            {exportError !== null && (
              <p
                style={{
                  color: 'var(--destructive, oklch(0.55 0.18 30))',
                  marginTop: '0.5rem',
                  fontSize: '0.85rem',
                }}
              >
                {exportError}
              </p>
            )}
            {exportResult !== null && (
              <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                Your export is ready.{' '}
                <a href={exportResult.signedUrl} style={{ color: 'var(--primary)' }}>
                  Download archive
                </a>{' '}
                (expires {fmtDate(exportResult.expiresAt)}).
              </p>
            )}
          </section>

          {/* 4. Delete account */}
          <section
            style={{ ...sectionCard, borderColor: 'var(--destructive, oklch(0.55 0.18 30))' }}
            aria-label="Delete account"
          >
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Delete my account
            </h2>
            {deleteStatus?.status === 'pending' ? (
              <>
                <p style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                  Your account is scheduled for deletion on{' '}
                  <strong>{fmtDate(deleteStatus.scheduledFor ?? '')}</strong>. You can cancel any
                  time before then.
                </p>
                <button
                  type="button"
                  onClick={() => void handleCancelDelete()}
                  disabled={deleteBusy}
                  style={{
                    padding: '0.55rem 1.1rem',
                    borderRadius: '999px',
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    fontSize: '0.9rem',
                    cursor: deleteBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  {deleteBusy ? 'Cancelling…' : 'Cancel deletion'}
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                  Deleting your account will erase your customer profile, pets, bookings, and
                  message history with this kennel. Financial records are retained for tax
                  compliance.
                </p>
                <p style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  After you confirm, we will wait <strong>24 hours</strong> before running the
                  delete — you can cancel any time during that window.
                </p>
                {deleteConfirmStep === 0 ? (
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmStep(1)}
                    style={{
                      padding: '0.55rem 1.1rem',
                      borderRadius: '999px',
                      background: 'var(--destructive, oklch(0.55 0.18 30))',
                      color: 'white',
                      border: 'none',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                    }}
                  >
                    Delete my account
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmStep(0)}
                      style={{
                        padding: '0.55rem 1.1rem',
                        borderRadius: '999px',
                        border: '1px solid var(--border)',
                        background: 'var(--surface)',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete()}
                      disabled={deleteBusy}
                      style={{
                        padding: '0.55rem 1.1rem',
                        borderRadius: '999px',
                        background: 'var(--destructive, oklch(0.55 0.18 30))',
                        color: 'white',
                        border: 'none',
                        fontSize: '0.9rem',
                        cursor: deleteBusy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {deleteBusy ? 'Requesting…' : 'Confirm — delete my account'}
                    </button>
                  </div>
                )}
              </>
            )}
            {deleteError !== null && (
              <p
                style={{
                  color: 'var(--destructive, oklch(0.55 0.18 30))',
                  marginTop: '0.5rem',
                  fontSize: '0.85rem',
                }}
              >
                {deleteError}
              </p>
            )}
          </section>
        </div>
      </div>
    </BrandedShell>
  );
}
